import { DashboardHeader } from "~/modules/dashboard/components/DashboardHeader.tsx";
import { KpiFiguresBox } from "~/modules/dashboard/components/KpiFiguresBox.tsx";
import { ProjectCards } from "~/modules/dashboard/components/ProjectCards.tsx";
import { SickDayBox } from "~/modules/dashboard/components/SickDayBox.tsx";
import { TimeTrackingSection } from "~/modules/dashboard/components/TimeTrackingCalendar/TimeTrackingSection.tsx";
import { UpcomingSection } from "~/modules/dashboard/components/UpcomingSection.tsx";
import { VacationBox } from "~/modules/dashboard/components/VacationBox.tsx";
import { DashboardCalendarProvider } from "~/modules/dashboard/hooks/DashboardCalendarProvider.tsx";
import { useDashboardCalendar } from "~/modules/dashboard/hooks/useDashboardCalendar.ts";
import { DashboardUserProvider, useDashboardUser } from "~/modules/dashboard/hooks/useDashboardUser.tsx";

// From `lg`, each column is one wrapper, so the main column (header, projects, time tracking) and the
// right column (KPIs, vacation, sick days) stack with no gap. Rows of one grid would share their height
// across the columns. On a phone, both wrappers are `contents`, and `order` sets the phone order: header,
// KPIs, projects, time tracking, vacation, sick days. The KPIs are the only section out of DOM order, and
// they have no focusable element, so the tab order on a phone is the visual order.
// The right column is 1/3 at `lg` and 1/4 only from `xl`: beside the sidebar, a quarter at `lg` is too
// narrow for the figure rows, and their values overflow.
function Dashboard() {
	const user = useDashboardUser();
	const { calendarRef } = useDashboardCalendar();

	if (!user) return null;

	// A user with no staff member has no vacation and no sick days, and their reads need a staff
	// member id. So the vacation box and the sick-day box do not render, and no read goes out.
	const staffMemberId = user.staffMemberId;

	return (
		<div className="mx-auto w-full max-w-7xl p-4 sm:px-6 lg:px-8">
			<div className="grid grid-cols-1 gap-6 lg:grid-cols-3 xl:grid-cols-4">
				<div className="contents lg:col-span-2 lg:block lg:min-w-0 lg:space-y-6 xl:col-span-3">
					<div className="order-1 min-w-0">
						<DashboardHeader user={user} />
						<UpcomingSection />
					</div>
					<section aria-label="Projekte"
							 className="order-3 min-w-0">
						<ProjectCards />
					</section>
					<section ref={calendarRef}
							 aria-label="Zeiterfassung"
							 className="order-4 min-w-0">
						<TimeTrackingSection user={user} />
					</section>
				</div>
				<div className="contents lg:block lg:min-w-0 lg:space-y-6 lg:border-l lg:border-dashed lg:border-gray-300 lg:pl-6">
					<section aria-label="Kennzahlen"
							 className="order-2 min-w-0">
						<KpiFiguresBox />
					</section>
					{staffMemberId && (
						<>
							<section aria-label="Urlaub"
									 className="order-5 min-w-0">
								<VacationBox staffMemberId={staffMemberId} />
							</section>
							<section aria-label="Krankheitstage"
									 className="order-6 min-w-0">
								<SickDayBox staffMemberId={staffMemberId} />
							</section>
						</>
					)}
				</div>
			</div>
		</div>
	);
}

export function DashboardView() {
	return (
		<DashboardUserProvider>
			<DashboardCalendarProvider>
				<Dashboard />
			</DashboardCalendarProvider>
		</DashboardUserProvider>
	);
}
